import type { FC, ReactElement } from 'react';
import { memo, useEffect } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { useHelpTranslationServiceConnected } from '../../services/helpTranslations/reactBindings/hooks';
import { useRtpFetcherConnected } from '../../store/apiHooks/useRtpFetcherConnected';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { PassiveComponentContext } from '../context';
import { ErrorBoundary } from '../errorBoundary/ErrorBoundary';
import { Loader } from '../loader/Loader';
import type { NavDrawerContentProps } from '../nav/types';

import { HelpAccordion } from './accordion/HelpAccordion';
import { HelpContextProviderConnected } from './context/HelpContextProviderConnected';
import { HelpErrorMessage } from './errorMessage/HelpErrorMessage';
import { useHelpChaptersToHideConnected } from './hooks/useHelpChapterssToHide';

const HelpContentConnected: FC = () => {
  const isRTL = useSettingsSlice.getIsCurrentLanguageRTL();
  const {
    config: { helpConfig },
  } = useGameConfig();
  const { isReady, hasFailed, forceUpdate } = useHelpTranslationServiceConnected();
  const chaptersToHide = useHelpChaptersToHideConnected();

  if (hasFailed) {
    return <HelpErrorMessage isError onClick={forceUpdate} />;
  }

  if (!isReady) {
    return <Loader />;
  }

  return (
    <HelpContextProviderConnected customStateToPropsMap={helpConfig.mapContextToStore}>
      <HelpAccordion helpConfig={helpConfig} chaptersToHide={chaptersToHide} isRTL={isRTL} />
    </HelpContextProviderConnected>
  );
};

export const HelpConnected: FC<NavDrawerContentProps> = memo((): ReactElement => {
  const rtpFetcher = useRtpFetcherConnected(false);

  useEffect(() => {
    rtpFetcher();
  }, [rtpFetcher]);

  return (
    <ErrorBoundary fallback={<Loader />}>
      <PassiveComponentContext.Provider value>
        <HelpContentConnected />
      </PassiveComponentContext.Provider>
    </ErrorBoundary>
  );
});

HelpConnected.displayName = 'HelpConnected';
